import { InjectionToken, Type } from '@angular/core';
import { PlaybackController } from './playback-controller';

/**
 * Pluggable toolbar registry contracts.
 *
 * The toolbar is a thin HOST that renders an ordered, per-sensor-filtered list of
 * control plugins. Whether a control appears for a sensor is pure configuration
 * (`SensorToolbarConfig`); adding a new control means registering a descriptor
 * (`TOOLBAR_CONTROLS`) — no host edits.
 */

/**
 * `track` is Voyage Radar's per-vessel track bar — a third toolbar that stacks
 * above the live bar while a vessel's path is being replayed.
 *
 * `vessel` is Voyage Radar's located-vessel bar — raised for a vessel found via
 * worldwide search (which can exist with NO snapshot on the map at all), naming
 * the hull and carrying its per-vessel actions. See VoyageVesselController.
 */
export type ToolbarSlot = 'live' | 'playback' | 'track' | 'vessel';

/** Injected into every control component; gives it the shared controller + its config. */
export interface ToolbarControlContext {
  controller: PlaybackController;
  slot: ToolbarSlot;
  options: Record<string, any>;
}

/**
 * DI token the host uses to hand each dynamically-rendered control its context.
 * (Angular 14's `NgComponentOutlet` has no input binding, so context is provided
 * via a per-control `Injector` instead of an `@Input`.)
 */
export const TOOLBAR_CONTROL_CONTEXT = new InjectionToken<ToolbarControlContext>(
  'TOOLBAR_CONTROL_CONTEXT',
);

/** Contract every control plugin component implements. */
export interface ToolbarControl {
  context: ToolbarControlContext;
}

/** Registration record for a control plugin (provided via TOOLBAR_CONTROLS multi-token). */
export interface ToolbarControlDescriptor {
  id: string;
  component: Type<ToolbarControl>;
  /** Which toolbars this control may appear in. */
  slots: ToolbarSlot[];
  defaultOrder: number;
}

export const TOOLBAR_CONTROLS = new InjectionToken<ToolbarControlDescriptor[]>(
  'TOOLBAR_CONTROLS',
);

/** Per-control entry inside a sensor's toolbar configuration. */
export interface ToolbarControlConfig {
  id: string;
  enabled?: boolean;
  order?: number;
  options?: Record<string, any>;
}

/** Full per-sensor toolbar configuration (which controls appear in each slot). */
export interface SensorToolbarConfig {
  operatorKey: string;
  live: ToolbarControlConfig[];
  playback: ToolbarControlConfig[];
  /** Optional per-vessel track bar (Voyage Radar); omitted by other sensors. */
  track?: ToolbarControlConfig[];
  /** Optional located-vessel bar (Voyage Radar search); omitted by other sensors. */
  vessel?: ToolbarControlConfig[];
}

export const SENSOR_TOOLBAR_CONFIGS = new InjectionToken<SensorToolbarConfig[]>(
  'SENSOR_TOOLBAR_CONFIGS',
);
